"use client";

import { GalleryI } from "@/interfaces/galleries/Galleries";
import { useState } from "react";
import { BiChevronDown, BiChevronUp } from "react-icons/bi";

interface SearchResultsSummaryProps {
  galleries: Record<string, GalleryI[]>;
  totalGalleries: number;
}

export function SearchResultsSummary({
  galleries,
  totalGalleries,
}: SearchResultsSummaryProps) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState(false);

  const toggleMobile = () => setIsMobileOpen(!isMobileOpen);
  const toggleDesktop = () => setIsDesktopCollapsed(!isDesktopCollapsed);

  const scrollToGallery = (galleryId: string) => {
    document.getElementById(`gallery-${galleryId}`)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    setIsMobileOpen(false);
  };
  const renderContent = () => (
    <div className="space-y-3">
      <div className="text-sm text-gray-300">
        <span className="font-semibold">Total:</span> {totalGalleries} galleries
      </div>

      {Object.entries(galleries).length > 0 ? (
        Object.entries(galleries).map(([cat, galleriesInCat]) => (
          <div key={cat} className="space-y-2">
            <h3 className="text-lg font-bold text-white border-b border-gray-600 pb-1">
              {cat} ({galleriesInCat.length})
            </h3>
            <div className="space-y-1 pl-2">
              {galleriesInCat.map((gallery) => (
                <div
                  key={gallery._id}
                  className="text-sm text-gray-300 hover:text-white hover:bg-gray-800 cursor-pointer transition-all duration-200 py-2 px-3 rounded-md border-l-2 border-transparent hover:border-blue-500"
                  onClick={() => scrollToGallery(gallery._id)}
                >
                  <div className="font-medium">{gallery.name}</div>
                  <div className="text-xs text-gray-400">
                    {gallery.photoGallery?.length || 0} photos
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="text-gray-400 text-center py-4">
          No categories selected
        </div>
      )}
    </div>
  );

  if (galleries === null) return null;
  return (
    <>
      {/* DESKTOP */}
      <div className="hidden md:block sticky top-4 z-40 mb-6 m-4">
        <div className="bg-black/95 backdrop-blur-sm rounded-lg border shadow-xl m-4">
          <div
            className="flex gap-2 items-center justify-between p-4 cursor-pointer hover:bg-gray-800/50 transition-colors"
            onClick={toggleDesktop}
          >
            <h2 className="font-bold text-white text-lg">Search Results</h2>
            <div className="flex items-center gap-2 text-gray-400">
              <span className="text-sm">{totalGalleries} galleries</span>
              {isDesktopCollapsed ? (
                <BiChevronDown className="w-8 h-8" />
              ) : (
                <BiChevronUp className="w-8 h-8" />
              )}
            </div>
          </div>
          <div
            className={`transition-all duration-300 overflow-hidden ${
              isDesktopCollapsed ? "max-h-0" : "max-h-[500px]"
            }`}
          >
            <div className="p-4 pt-0 max-h-[450px] overflow-y-auto custom-scrollbar">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
      {/* MOBILE */}
      <div className="md:hidden">
        {isMobileOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setIsMobileOpen(false)}
          />
        )}

        <div
          className={`
                    fixed bottom-0 left-0 right-0 z-50
                    bg-black border-t border-gray-700 rounded-t-lg
                    transform transition-transform duration-300 ease-out
                    ${isMobileOpen ? "translate-y-0" : "translate-y-full"}
                    max-h-[80vh] flex flex-col
                `}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-black sticky top-0">
            Search Results
            <button
              onClick={() => setIsMobileOpen(false)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <BiChevronDown className="w-6 h-6" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">{renderContent()}</div>
        </div>

        <div className="fixed bottom-46 left-4 right-4 z-40">
          <button
            onClick={toggleMobile}
            className="w-full bg-black/90 backdrop-blur-sm text-white p-4 rounded-lg 
                                 border border-gray-700 shadow-lg flex items-center justify-between
                                 hover:bg-gray-800 transition-colors max-w-sm mx-auto"
          >
            <span className="font-medium">Open search Results</span>
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #1f2937;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #4b5563;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #6b7280;
        }
      `}</style>
    </>
  );
}
